import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { Fragment } from 'react';
import { cn } from '@/lib/utils';
import type { MetricsChoice } from '@/services/dynamicFlowsService';
import { formatNumber, formatPercent } from './format';
import { groupChoicesByStep } from './metricsUtils';

export function ChoicesTable({ choices }: { choices: MetricsChoice[] }) {
  useLanguage();
  const groups = groupChoicesByStep(choices);

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-[#D9E0EA] bg-white p-[18px]">
      <h2 className="text-[15px] font-bold text-[#111827]">{translateInterfaceText("Por opção")}</h2>
      {groups.length === 0 ? (
        <p className="text-sm text-[#4B5563]">{translateInterfaceText("O fluxo ainda não tem opções.")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-[#4B5563]">
                <th scope="col" className="border-b border-[#E5E7EB] px-2.5 py-2 font-semibold">
                  {translateInterfaceText("Etapa")}</th>
                <th scope="col" className="border-b border-[#E5E7EB] px-2.5 py-2 font-semibold">
                  {translateInterfaceText("Opção")}</th>
                <th scope="col" className="border-b border-[#E5E7EB] px-2.5 py-2 font-semibold">
                  {translateInterfaceText("Leva para")}</th>
                <th scope="col" className="border-b border-[#E5E7EB] px-2.5 py-2 text-right font-semibold">
                  {translateInterfaceText("Cliques")}</th>
                <th scope="col" className="border-b border-[#E5E7EB] px-2.5 py-2 text-right font-semibold">
                  {translateInterfaceText("De quem viu as opções")}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <Fragment key={group.node_id}>
                  {group.choices.map((choice, index) => {
                    const last = index === group.choices.length - 1;
                    const rowBorder = last ? 'border-b border-[#E5E7EB]' : 'border-b border-[#F1F5F9]';
                    return (
                      <tr key={choice.choice_id}>
                        {index === 0 && (
                          <th
                            scope="rowgroup"
                            rowSpan={group.choices.length}
                            className="border-b border-[#E5E7EB] px-2.5 py-2.5 text-left align-top font-semibold text-[#111827]"
                          >
                            {group.node_label}
                          </th>
                        )}
                        <td className={cn('px-2.5 py-2.5', rowBorder)}>
                          {choice.letter ? `${choice.letter} · ` : ''}
                          {choice.label}
                        </td>
                        <td
                          className={cn(
                            'px-2.5 py-2.5',
                            rowBorder,
                            choice.target_type === 'exit' ? 'font-semibold text-[#1E3A8A]' : 'text-[#4B5563]',
                          )}
                        >
                          {choice.target_label}
                        </td>
                        <td className={cn('px-2.5 py-2.5 text-right tabular-nums', rowBorder)}>
                          {formatNumber(choice.clicks)}
                        </td>
                        <td className={cn('px-2.5 py-2.5 text-right tabular-nums', rowBorder)}>
                          {formatPercent(choice.rate)}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
